import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUser } from '@/contexts/UserContext';
import { useToast } from '@/hooks/useToast';
import { User, ArrowLeft, Shield } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

export default function AdminProfile() {
  const { user } = useUser();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [adminInfo, setAdminInfo] = useState({
    currentUsername: 'admin',
    currentPassword: 'admin123',
    id: 'admin-1'
  });
  
  const [formData, setFormData] = useState({
    username: 'admin',
    password: ''
  });

  useEffect(() => {
    try {
      const savedUsers = localStorage.getItem('shiphub-users');
      let users = savedUsers ? JSON.parse(savedUsers) : [];
      
      console.log('Users in localStorage:', users);
      
      // Find admin user
      let admin = users.find((u: any) => u.role === 'admin');
      
      if (!admin) {
        // Create admin user if doesn't exist
        admin = {
          id: 'admin-1',
          username: 'admin',
          password: 'admin123',
          role: 'admin',
          createdAt: new Date().toISOString()
        };
        users.push(admin);
        localStorage.setItem('shiphub-users', JSON.stringify(users));
        console.log('Created admin user:', admin);
      } else {
        console.log('Found existing admin:', admin);
      }
      
      // Set form data
      setAdminInfo({
        currentUsername: admin.username,
        currentPassword: admin.password,
        id: admin.id
      });
      
      setFormData({
        username: admin.username,
        password: ''
      });
      
    } catch (error) {
      console.error('Error loading admin:', error);
    }
  }, []);

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Updating admin credentials to:', formData);

    try {
      const savedUsers = localStorage.getItem('shiphub-users');
      const users = savedUsers ? JSON.parse(savedUsers) : [];
      
      // Find admin by role
      const adminIndex = users.findIndex((u: any) => u.role === 'admin');
      
      if (adminIndex !== -1) {
        // Update admin
        users[adminIndex].username = formData.username;
        if (formData.password) {
          users[adminIndex].password = formData.password;
        }
        
        // Save
        localStorage.setItem('shiphub-users', JSON.stringify(users));
        console.log('Updated users:', users);
        
        // Update local state
        setAdminInfo({
          currentUsername: formData.username,
          currentPassword: formData.password || users[adminIndex].password,
          id: users[adminIndex].id
        });

        toast({
          title: "Success!",
          description: "Admin credentials updated successfully"
        });

        setFormData(prev => ({ ...prev, password: '' }));
      } else {
        toast({
          title: "Error",
          description: "Admin user not found",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error", 
        description: "Update failed: " + error.message,
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center space-x-4">
          <Link to="/" className="text-gray-600 hover:text-gray-900 flex items-center">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Link>
        </div>

        {/* Current Info Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-3">
              <div className="bg-blue-600 p-3 rounded-full">
                <User className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle>Admin Account Information</CardTitle>
                <p className="text-sm text-gray-500">View your current admin details</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-xs text-gray-500">Role</Label>
                  <p className="font-medium">Administrator</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">User ID</Label>
                  <p className="font-medium font-mono text-sm">{adminInfo.id}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Current Username</Label>
                  <p className="font-medium">{adminInfo.currentUsername}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Current Password</Label>
                  <p className="font-medium">{'•'.repeat(adminInfo.currentPassword.length)}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Update Form Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-3">
              <div className="bg-orange-600 p-3 rounded-full">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle>Update Login Credentials</CardTitle>
                <p className="text-sm text-gray-500">Change your username and password</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdate} className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                    placeholder="Enter new username"
                    required
                  />
                  <p className="text-xs text-gray-500">This will be your new login username</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">New Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Enter new password (leave empty to keep current)"
                  />
                  <p className="text-xs text-gray-500">Leave empty to keep your current password</p>
                </div>
              </div>

              <div className="flex justify-end">
                <Button type="submit">
                  Update Credentials
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}